/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Network layer service
 */

namespace Application.Services {
    'use strict';

    import NetworkNode = Models.NetworkNode;
    import IHalList = Models.IHalList;
    import HalConverter = Models.HalConverter;
    import IHalElement = Models.IHalElement;
    import IPaginatedList = Models.IPaginatedList;

    /**
     * NetworkNodeService
     */
    export class NetworkNodeService {
        public static $name: string = "networkNodeService";
        public static $inject: string[] = ['networkNodeTypeService',
                                           'backendService',
                                           'healthService',
                                           'vnoService',
                                           'deviceCredentialsService',
                                          ];

        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = '';
        private backendUrl: string = this.backendBaseUrl + this.servicePath + 'networkNodes';
        private workflowUrl: string = this.backendBaseUrl + this.servicePath + 'workflow';
        private nodeTypeMap: INodeTypeMap = {};


        constructor(private networkNodeTypeService: NetworkNodeTypeService,
                    private backendService: BackendService,
                    private healthService: HealthService,
                    private vnoService: VnoService,
                    private deviceCredentialsService: Services.DeviceCredentialsService) {
        }

        public getFilteredNetworkNodes(page: number, size: number, sortType: String, direction: string,
                                       textSearch: string, typeIds: number[], vnoIds: number[],
                                       statuses: string[], maintenance: string[]): ng.IPromise<IPaginatedList<NetworkNode>> {
            let backendFilter: string = '/filterAll?';
            // If status is online or offline we show only the networknodes that are currently enabled
            if (statuses.length === 1) {
                backendFilter = '/filterAllEnabled?';
            }
            return this.searchWithEndpoint(page, size, sortType, direction, textSearch,
                                                typeIds, vnoIds, statuses, backendFilter, maintenance.map((value: string) => value.toLowerCase()));
        }

        public getEnabledFilteredNetworkNodes(page: number,
                                              size: number,
                                              sortType: String,
                                              direction: string,
                                              textSearch: string,
                                              typeIds: number[],
                                              vnoIds: number[],
                                              statuses: string[]): ng.IPromise<IPaginatedList<NetworkNode>> {
            let backendFilter: string = '/filterAllEnabled?';
            return this.searchWithEndpoint(page, size, sortType, direction, textSearch, typeIds, vnoIds,
                                            statuses, backendFilter);
        }

        public getAllPaginatedNetworkNodesPromise(page?: number, size?: number, sortType?: String,
                                                  direction?: string, textSearch?: string): ng.IPromise<IPaginatedList<NetworkNode>> {
            let backendFilter: string = textSearch ? '/filteredByText?' : '/filtered?';
            let url: string = this.backendUrl + backendFilter;
            url = page ? url += '&page=' + page : url;
            url = size ? url += '&size=' + size : url;
            url = sortType ? url += '&sortType=' + sortType : url;
            url = direction ? url += '&direction=' + direction : url;
            url = textSearch ? url += '&text=' + textSearch : url;
            return this.backendService
                .get(url, true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let convertedList: Models.IPaginatedList<NetworkNode>;
                    convertedList = HalConverter.convertPaginatedList(result.data, NetworkNode.Empty);
                    convertedList.elements.forEach((element: NetworkNode) => {
                        this.burnishNode(element);
                    });
                    return convertedList;
                });
        }

        public getById(id: number, callback: (message: NetworkNode) => void): void {
            this.getByIdPromise(id)
                .then((data: NetworkNode) => {
                    callback(data);
                });
        }

        /**
         * TO DO: when deleting a node from network map, this function is being sent as a callback to broadcastEvent() 
         * in Stomp Service. Ideally, we should avoid cache only when deleting event is triggered (bug 5089), but delete event in network map 
         * is being registered as saveNetworkMap (another bug). So, the solution for now is avoid cache calls in every event call made by stomp Service
         * @param id 
         */
        public getByIdPromise(id: number): ng.IPromise<NetworkNode> {
            return this.backendService
                .get(this.backendUrl + '/' + id + "?nocache="+ Math.random() * 9999999999 )
                .then((data: ng.IHttpPromiseCallbackArg<IHalElement<NetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = data.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, NetworkNode.Empty);
                    return this.burnishNode(converted);
                });
        }

        public getAllNetworkNodes(callback: (messages: NetworkNode[]) => void): void {
            this.getAllNetworkNodesPromise()
                .then((data: NetworkNode[]) => {
                    callback(data);
                });
        }

        public async getMonitorNodes(): Promise<NetworkNode[]>{
            return (await this.getAllNetworkNodesPromise()).filter(
                (node: NetworkNode) => {
                    if(node.config.monitorMode || node.networkNodeTypeId == 28){
                        return true;
                    }else{
                        return false;
                    }
                });
        }

        public getAllEnabledNetworkNodesPromise(): ng.IPromise<NetworkNode[]> {
            let url: string = this.backendUrl + '/enabled?size=999999999';
            return this.backendService
                .get(url)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let halElement: IHalList<NetworkNode> = result.data;
                    let converted: NetworkNode[] = HalConverter.convertList(halElement, NetworkNode.Empty);
                    converted.forEach((element: NetworkNode) => {
                        this.burnishNode(element);
                    });
                    return converted;
                });
        }

        public getAllNetworkNodesPromise(): ng.IPromise<NetworkNode[]> {
            let url: string = this.backendUrl + '/filtered?size=999999999';
            return this.backendService
                .get(url)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let halElement: IHalList<NetworkNode> = result.data;
                    let converted: NetworkNode[] = HalConverter.convertList(halElement, NetworkNode.Empty);
                    converted.forEach((element: NetworkNode) => {
                            this.burnishNode(element);
                    });
                    return converted;
                });
        }

        public getByIdString(id: string, callback: (result: NetworkNode) => void): void {
            this.getByHref(this.backendUrl + '/' + id, callback);
        }

        public getByHref(href: string, callback: (result: NetworkNode) => void): void {
            this.getNetworkNodePromise(href)
                .then((element: NetworkNode) => {
                    this.burnishNode(element);
                    callback(element);
                }
            );
        }

        public getByMftSerialNumber(serial: string, callback: (result: NetworkNode) => void,
                                    error: (reason: ngCustom.IReason) => void): void {
            this.backendService
                .get(this.backendUrl + "/search/findByMftSerialNumber?mftSerialNumber=" + serial, false, undefined, false, false)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Models.INetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = result.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, NetworkNode.Empty);
                    this.burnishNode(converted);
                    callback(converted);
                })
                .catch((reason: ngCustom.IReason) => {
                    error(reason);
                });
        }

        public getByMftSerialNumberPromise(serial: string): ng.IPromise<NetworkNode> {
            return this.backendService
                .get(this.backendUrl + "/search/findByMftSerialNumber?mftSerialNumber=" + serial, false, undefined, false, false)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Models.INetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = result.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, NetworkNode.Empty);
                    this.burnishNode(converted);
                    return converted;
                });
        }

        /**
         * This method checks if there is a node with the same serial already stored in the database
         *
         * @param serial serial of the node to be checked
         * @return boolean true if the serial is not already in the database
         */
        public checkUsedMftSerialNumberPromise(serial: string): ng.IPromise<boolean> {
            return this.backendService
                .get(this.backendUrl + "/usedSerial?serial=" + serial, false, undefined, false, false)
                .then((result: ng.IHttpPromiseCallbackArg<Boolean>) => {
                    return result.data;
                });
        }

        /**
         * This method checks if there is a node with the same name already stored in the database
         *
         * @param name of the node to be checked
         * @return boolean true if the name is not already in the database
         */
        public existsNodeName(name: string): ng.IPromise<boolean> {
            name = name ? name.toUpperCase() : "";
            return this.backendService
                .get(this.backendUrl + "/findByName?name=" + name, false, undefined, false, false)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let halElement: IHalList<NetworkNode> = result.data;
                    if(halElement[0] !== undefined) {
                        return true;
                    }
                    else {
                        return false;
                    }
                });
        }

        public saveNetworkNode(node: Application.Models.INetworkNode): ng.IPromise<Models.NetworkNode> {
            if (!node.href || node.href === undefined || node.href === '') {
                return this.createNetworkNode(node);
            } else {
                return this.updateNetworkNode(node);
            }
        }

        public createNetworkNode(node: Application.Models.INetworkNode): ng.IPromise<Models.NetworkNode> {
            return this.backendService
                .post(this.backendUrl, {
                    name: node.name,
                    creationDate: node.creationDate,
                    mftSerialNumber: node.mftSerialNumber,
                    physicalLocation: node.physicalLocation,
                    type: node.type.href,
                    geoElement: node.geoElement,
                    x: node.x,
                    y: node.y,
                    networkInterfaces: node.networkInterfaces,
                    hostname: node.hostname,
                    alias: node.alias,
                    config: node.config,
                    contactInfo: node.contactInfo,
                    workflowMode: 'operation',
                    useMag: node.useMag,
                    preferredMag: node.preferredMag,
                    organizationId: node.organizationId,
                })
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Models.INetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = result.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, Models.NetworkNode.Empty);

                    if(node.deviceCredentials) { //We only execute this block, when importing a new node, to put the exported credentials.
                        this.deviceCredentialsService.getDeviceCredentialsFor(converted).then(defaultCredentials => {
                            node.deviceCredentials.forEach((credential, index) => {
                                credential.href = defaultCredentials[index].href;
                                credential.deviceHref = defaultCredentials[index].deviceHref;
                                this.deviceCredentialsService.saveCredential(credential)
                            });
                        });
                    }

                    return this.burnishNode(converted);
                 });
        }

        public updateNetworkNode(node: Application.Models.INetworkNode): ng.IPromise<Models.NetworkNode> {
            node = this.setNetworkHrefs(node as Application.Models.NetworkNode);
            return this.backendService
                .put(node.href, {
                    name: node.name,
                    creationDate: node.creationDate,
                    mftSerialNumber: node.mftSerialNumber,
                    physicalLocation: node.physicalLocation,
                    type: node.type.href,
                    geoElement: node.geoElement,
                    x: node.x ? node.x : null, // tslint:disable-line
                    y: node.y ? node.y : null, // tslint:disable-line
                    hostname: node.hostname,
                    alias: node.alias,
                    config: node.config,
                    contactInfo: node.contactInfo,
                    networkInterfaces: node.networkInterfaces,
                    workflowMode: node.workflowMode,
                    useMag: node.useMag,
                    preferredMag: node.preferredMag,
                })
                .then ((result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Models.INetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = result.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, NetworkNode.Empty);
                    return this.burnishNode(converted);
                 }
            );
        }

        public addPositionToNode(href: string, x: number, y: number): ng.IPromise<NetworkNode> {
            return this.getNetworkNodePromise(href)
                .then((networkNode: NetworkNode) => {
                    networkNode.x = x;
                    networkNode.y = y;
                    return this.updateNetworkNode(networkNode);
                 });
        }

        public deleteNetworkNode(node: Application.Models.INetworkNode): ng.IPromise<boolean> {
            return this.backendService.delete(node.href).then(() => true);
        }

        public startSupportTest(node: Application.Models.INetworkNode): ng.IPromise<void> {
            return this.backendService.post(node.href + "/startSupportTest", {}).then(() => {
                return;
            });
        }

        public endSupportTest(node: Application.Models.INetworkNode, summary: string): ng.IPromise<void> {
            return this.backendService.post(node.href + "/endSupportTest", {summary: summary}).then(() => {
                return;
            });
        }

        public getNodeSpeedTest(node: Application.Models.INetworkNode): ng.IPromise<Models.ISpeedTestResult> {
            return this.backendService
                .get(node.href + "/speedTest", false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ISpeedTestResult>) => {
                    return data.data;
                });
        }

        public getNodePingTest(node: Application.Models.INetworkNode, ip: string): ng.IPromise<Models.ITestResult> {
            return this.backendService
                .get(node.href + "/ping?ip=" + ip, false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITestResult>) => {
                    return data.data;
                });
        }

        public getNodeTracerouteTest(node: Application.Models.INetworkNode, ip: string): ng.IPromise<Models.ITestResult> {
            return this.backendService
                .get(node.href + "/traceroute?ip=" + ip, false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITestResult>) => {
                    return data.data;
                });
        }

        public getUdpEndToEndTest(node: Application.Models.INetworkNode, target: Application.Models.INetworkNode, port: number) {
            return this.backendService
                .get(node.href + "/udpPing/" + target.id + "?port=" + port, false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITestResult>) => {
                    return data.data;
                });
        }

        public getUdpEndToEndTestInterval(node: Application.Models.INetworkNode, target: Application.Models.INetworkNode,
                                          port: number, interval: number) {
            return this.backendService
                .get(node.href + "/udpPing/" + target.id + "?port=" + port + "&interval=" + interval, false, undefined, false, false)
                .then((data: ng.IHttpPromiseCallbackArg<Models.ITestResult>) => {
                    return data.data;
                });
        }

        public getAllByNetworkNodeTypeId(id: number): ng.IPromise<NetworkNode[]> {
            return this.backendService
                .get(this.backendUrl + "/search/findAllByTypeId?typeId=" + id, false, undefined, false, false)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IHalList<Models.INetworkNode[]>>) => {
                    let halList: IHalList<NetworkNode> = result.data;
                    let converted: NetworkNode[] = HalConverter.convertList(halList, NetworkNode.Empty);
                    converted.forEach((node: Models.NetworkNode) => {
                        this.burnishNode(node);
                    });
                    return converted;
                });
        }

        public updateWorkflowMode(node: Models.INetworkNode): ng.IPromise<Models.INetworkNode> {
            let id: number = Common.Utils.idFromHref(node.href);
            let workflowMode: string = node.isMaintenance ? "setMaintenance" : "setOperation";
            return this.backendService
                .put(this.workflowUrl + "/node/" + id + "/" + workflowMode, {}, false, undefined, false)
                .then((res) => res as Models.INetworkNode);

        }

        public getNetworkNodePromise(href: string): ng.IPromise<NetworkNode> {
            return this.backendService
                .get(href)
                .then((data: ng.IHttpPromiseCallbackArg<IHalElement<NetworkNode>>) => {
                    let halElement: IHalElement<NetworkNode> = data.data;
                    let converted: NetworkNode = HalConverter.convertElement(halElement, NetworkNode.Empty);
                    return this.burnishNode(converted);
                });
        }

        public changeNetworkNodeOrganization(href: string, organizationId: number): ng.IPromise<boolean> {
            return this.backendService
                .put(href + "/organization/" + organizationId, {})
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public addNetworkNodeOrganization(href: string, organizationId: number): ng.IPromise<boolean> {
            return this.backendService
                .post(href + "/organization/" + organizationId, {})
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public removeNetworkNodeOrganization(href: string, organizationId: number): ng.IPromise<boolean> {
            return this.backendService
                .delete(href + "/organization/" + organizationId)
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public changeNetworkNodeOrganizations(href: string, organizationIds: number[]): ng.IPromise<boolean> {
            return this.backendService
                .put(href + "/organizations", organizationIds)
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return result.data;
                });
        }

        public getNetworkNodeOrganizationIds(href: string): ng.IPromise<number[]> {
            return this.backendService.get(href + "/organization")
                .then((res: ng.IHttpPromiseCallbackArg<number[]>) => {
                    return res.data;
                });
        }

        public getAllNetworkNodesByOrganizationId(id: number,
                                                  page?: number,
                                                  size?: number,
                                                  sortType?: string,
                                                  direction?: string,
                                                  textSearch?: string): ng.IPromise<IPaginatedList<NetworkNode>> {
            let url: string = this.backendUrl + '/organization/' + id; // + backendFilter;
            url = page ? url += '&page=' + page : url;
            url = size ? url += '&size=' + size : url;
            url = sortType ? url += '&sortType=' + sortType : url;
            url = direction ? url += '&direction=' + direction : url;
            url = textSearch ? url += '&text=' + textSearch : url;
            url = url.replace('&', '?');
            return this.backendService.get(url).then(
                (result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let convertedList: IPaginatedList<NetworkNode>;
                    convertedList = HalConverter.convertPaginatedList(result.data, NetworkNode.Empty);
                    convertedList.elements.forEach((element: NetworkNode) => {
                        this.burnishNode(element);
                    });
                    return convertedList;
                });
        }

        public numberOfNodesWhereNodeIsAssignedAsMag(href: string): ng.IPromise<number> {
            return this.backendService
                .get(this.backendUrl + "/search/numberOfNodesWhereNodeIsAssignedAsMag?networkNodeHref=" + href,
                    false, undefined, false, false)
                .then((response: ng.IHttpPromiseCallbackArg<number>) => {
                    return response.data;
                });
        }

        public getNetworkNodeIcon(networkNodeTypeHref: string): string {
            if (networkNodeTypeHref === undefined || networkNodeTypeHref === '') {
                return 'images/iconmonstr-server-4-240-not-checked.png';
            } else {
                return `images/nodeTypes/${Common.Utils.idFromHref(networkNodeTypeHref)}.png`;
            }
        }

        public getMgmtServers(element: NetworkNode): ng.IPromise<Models.IMgmtServers> {
            return this.backendService
                .get(this.backendUrl + "/" + element.id + "/managementServer")
                .then((response: ng.IHttpPromiseCallbackArg<Models.IMgmtServers>) => {
                    return response.data;
                });
        }

        public setMgmtServers(element: NetworkNode, mgmtIndex: number): ng.IPromise<boolean> {
            return this.backendService
                .post(this.backendUrl + "/" + element.id + "/managementServer/" + mgmtIndex, {})
                .then((response: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return response.data;
                });
        }

        public burnishNode(element: NetworkNode): NetworkNode {
            const href: string = element.href ? element.href : "/networkNodes/" + element.id;
            element.health = this.healthService.getNodeHealth(href);
            this.loadType(element);
            element.isMaintenance = element.workflowMode === 'maintenance';
            element.preferredMag = element.preferredMag ? element.preferredMag : undefined;
            return this.setNetworkHrefs(element);
        }

        private searchWithEndpoint(page: number,
                                   size: number,
                                   sortType: String,
                                   direction: string,
                                   textSearch: string,
                                   typeIds: number[],
                                   vnoIds: number[],
                                   statuses: string[],
                                   backendFilter: string,
                                   maintenance?: string[]): ng.IPromise<IPaginatedList<NetworkNode>> {
            let encodedParams: string = Common.Utils.encodeQueryData({
                page: page,
                size: size,
                sortType: sortType,
                direction: direction,
                text: textSearch,
                types: typeIds,
                vnos: vnoIds,
                healthStatus: statuses,
                maintenance: maintenance,
            });
            let url: string = this.backendUrl + backendFilter + encodedParams;
            return this.backendService.get(url, true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<NetworkNode>>) => {
                    let convertedList: Models.IPaginatedList<NetworkNode>;
                    convertedList = HalConverter.convertPaginatedList(result.data, NetworkNode.Empty);
                    convertedList.elements.forEach((element: NetworkNode) => {
                        this.burnishNode(element);
                    });
                    return convertedList;
                });
        }

        /**
         * Load the full NetworkNodeType element and set it into hte NetworkNode object.
         */
        private loadType(networkNode: NetworkNode): NetworkNode {
            if (this.nodeTypeMap[networkNode.type.href] === undefined) {
                this.networkNodeTypeService
                    .findByHref(networkNode.type.href)
                    .then((result: Models.INetworkNodeType) => {
                        this.nodeTypeMap[networkNode.type.href] = result;
                        networkNode.type = result;
                    });
            } else {
                networkNode.type = this.nodeTypeMap[networkNode.type.href];
            }
            return networkNode;
        }

        public fillMgmtServers(networkNode: Models.NetworkNode): ng.IPromise<void> {
            return this.getMgmtServers(networkNode).then((mgmtServers: Models.IMgmtServers) => {
                networkNode.mgmtServers = Object.keys(mgmtServers).map((serverKey: string) => {
                    return mgmtServers[serverKey];
                });
                return;
            });
        }

        private setNetworkHrefs(networkNode: NetworkNode): NetworkNode {
            networkNode.networkInterfaces.forEach((nic: Models.INetworkInterface) => {
                if (nic.networkConnectedTo) {
                    let id: number = nic.networkConnectedTo.id;
                    nic.networkConnectedTo.href = this.backendBaseUrl + this.servicePath + 'networks/' + id;
                }
            });
            return networkNode;
        }

        // private setNetworkNodeOrganization(element: NetworkNode): void {
        //     this.getNetworkNodeOrganization(element.href).then((response: Models.Vno) => {
        //         element.vno = response;
        //     });
        // }
    }

    interface INodeTypeMap {
        [key: string]: Models.NetworkNodeType;
    }
}
